/**
 * Minimal typed DOM builders for the overlay. No framework (tech-design §4) —
 * just enough sugar to keep the component code declarative and focused.
 */

type Child = Node | string;

interface ElementOptions {
  readonly class?: string;
  readonly text?: string;
  readonly testid?: string;
  readonly title?: string;
  readonly ariaLabel?: string;
}

/** Create an element with common attributes and children in one call. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options: ElementOptions = {},
  children: readonly Child[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (options.class !== undefined) {
    node.className = options.class;
  }
  if (options.text !== undefined) {
    node.textContent = options.text;
  }
  if (options.testid !== undefined) {
    node.dataset.testid = options.testid;
  }
  if (options.title !== undefined) {
    node.title = options.title;
  }
  if (options.ariaLabel !== undefined) {
    node.setAttribute('aria-label', options.ariaLabel);
  }
  for (const child of children) {
    node.append(child);
  }
  return node;
}

/** A `<button>` wired to `onClick`, with optional test id. */
export function button(
  label: string,
  onClick: () => void,
  options: { class?: string; testid?: string; title?: string } = {},
): HTMLButtonElement {
  const btn = el('button', {
    text: label,
    ...(options.class !== undefined ? { class: options.class } : {}),
    ...(options.testid !== undefined ? { testid: options.testid } : {}),
    ...(options.title !== undefined ? { title: options.title } : {}),
  });
  btn.type = 'button';
  btn.addEventListener('click', onClick);
  return btn;
}
